/**
 * Tiny color helper (replaces the withAlpha we used to import from Glide),
 * plus the F-17 READABILITY math.
 *
 * Why the readability math lives here: an F-17-bg fill is board content, the
 * theme (F-122 dark mode) is a per-user view preference, and the two are
 * chosen independently — so any pastel preset that reads fine as dark-on-white
 * turns near-white-on-pastel the moment a reader flips to dark mode. The grid
 * therefore derives the ink for a filled cell instead of inheriting
 * `--color-text`; everything it needs to do that is here, pure and testable.
 *
 * This is also where the app's hex NORMAL FORM lives (`normalizeHexColor` and
 * the opacity pair around it) — every color the UI writes into the doc passes
 * through it, so both the renderer and the writers agree on what a stored
 * color can look like. What the palette OFFERS (presets, selection previews)
 * stays in features/selection-toolbar/palette.ts.
 */

/** Straight (non-premultiplied) RGBA — channels 0–255, alpha 0–1. */
export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * User presence colors are always `#rrggbb` hex (core/identity.ts hslToHex);
 * anything else falls back to a CSS `color-mix`-free rgba via a safe default.
 */
export function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (m?.[1] !== undefined) {
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 0xff;
    const g = (n >> 8) & 0xff;
    const b = n & 0xff;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color; // unknown format — better opaque than broken
}

/**
 * The four hex shapes anything in the app accepts — `#rgb`, `#rgba`,
 * `#rrggbb`, `#rrggbbaa`, `#` optional and case-insensitive — widened to the
 * 6- or 8-digit lowercase form. `null` for everything else.
 */
function expandHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, "").toLowerCase();
  if (!/^(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(raw)) return null;
  return raw.length <= 4 ? raw.replace(/./g, (c) => c + c) : raw;
}

/**
 * Validate + normalize a user-typed hex color: `#abc`, `abc`, `#aabbcc`,
 * `AABBCC`, and the alpha forms `#abcd` / `#aabbccdd` (surrounding whitespace
 * tolerated) → lowercase `#aabbcc`, or `#aabbccdd` when the alpha is anything
 * short of opaque. Anything else → null (the input shows an invalid state,
 * nothing is written). Only hex is accepted — the value goes verbatim into the
 * CRDT and every client renders it, so this is the schema gate.
 *
 * A FULLY OPAQUE color always normalizes to the 6-digit form: `#aabbccff` and
 * `#aabbcc` are the same color, and collapsing them keeps preset-vs-custom
 * equality (and every pre-opacity value already stored in a doc) plain string
 * comparison.
 */
export function normalizeHexColor(input: string): string | null {
  const wide = expandHex(input);
  if (wide === null) return null;
  return wide.length === 8 && wide.endsWith("ff") ? `#${wide.slice(0, 6)}` : `#${wide}`;
}

/** Opacity as the UI exposes it: whole percent, 0 (clear) … 100 (opaque). */
export const OPACITY_MIN = 0;
export const OPACITY_MAX = 100;

function percentToAlphaByte(pct: number): number {
  const clamped = Math.min(Math.max(Math.round(pct), OPACITY_MIN), OPACITY_MAX);
  return Math.round((clamped / 100) * 255);
}

/**
 * The opacity baked into a hex color, as whole percent. A 6-digit value
 * (everything written before opacity existed) is opaque, and so is anything
 * this module would not have written.
 *
 * Exactly inverts the percent → alpha-byte mapping `withOpacity` uses over
 * 0…100: the byte scale is the finer of the two, so a slider round-trips
 * through storage without drifting a percent per drag.
 */
export function opacityOf(color: string): number {
  const wide = expandHex(color);
  if (wide === null || wide.length !== 8) return OPACITY_MAX;
  return Math.round((parseInt(wide.slice(6, 8), 16) / 255) * 100);
}

/**
 * `color` re-expressed at `pct` opacity, keeping its RGB. Returns the 6-digit
 * form at 100% (see `normalizeHexColor`), so re-opaquing a color lands back on
 * exactly the preset string it came from. `null` when `color` is not a hex
 * this module recognizes.
 */
export function withOpacity(color: string, pct: number): string | null {
  const wide = expandHex(color);
  if (wide === null) return null;
  const byte = percentToAlphaByte(pct);
  const rgb = wide.slice(0, 6);
  return byte >= 255 ? `#${rgb}` : `#${rgb}${byte.toString(16).padStart(2, "0")}`;
}

/**
 * Parse the ONLY color forms that reach the doc — `#rgb`, `#rgba`, `#rrggbb`,
 * `#rrggbbaa`, i.e. whatever `normalizeHexColor` lets through — plus the same
 * tokens.css-shaped hex the theme variables carry. Anything else → null, and
 * callers fall back rather than render from `NaN`.
 */
export function parseHexColor(input: string): Rgba | null {
  const wide = expandHex(input);
  if (wide === null) return null;
  const r = parseInt(wide.slice(0, 2), 16);
  const g = parseInt(wide.slice(2, 4), 16);
  const b = parseInt(wide.slice(4, 6), 16);
  const a = wide.length === 8 ? parseInt(wide.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
}

/**
 * Source-over composite of a translucent `fg` onto an OPAQUE `bg` — what the
 * eye actually sees where a partly-transparent fill sits on the grid surface.
 * The result is opaque (`bg`'s alpha is assumed 1, which it is for a theme
 * surface token).
 */
export function compositeOver(fg: Rgba, bg: Rgba): Rgba {
  const a = Math.min(Math.max(fg.a, 0), 1);
  return {
    r: fg.r * a + bg.r * (1 - a),
    g: fg.g * a + bg.g * (1 - a),
    b: fg.b * a + bg.b * (1 - a),
    a: 1,
  };
}

/** WCAG 2.x relative luminance (sRGB, 0 = black … 1 = white). */
export function relativeLuminance(c: Rgba): number {
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

/** WCAG 2.x contrast ratio between two OPAQUE colors — 1 (same) … 21 (b/w). */
export function contrastRatio(a: Rgba, b: Rgba): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** WCAG AA for normal-size body text — the grid renders cells at 13 px. */
export const MIN_TEXT_CONTRAST = 4.5;

/** The two inks the grid falls back to, in the theme's own text hues. */
export const INK_DARK = "#111827";
export const INK_LIGHT = "#f9fafb";

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 };

/* ------------------------------------------------------------------ */
/* F-122: a light-mode fill, re-rendered for a dark surface             */
/* ------------------------------------------------------------------ */

/** Hue 0-360, saturation and lightness 0-1. */
export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function rgbToHsl(c: Rgba): Hsl {
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  const h =
    max === r
      ? 60 * (((g - b) / d) % 6)
      : max === g
        ? 60 * ((b - r) / d + 2)
        : 60 * ((r - g) / d + 4);
  return { h: (h + 360) % 360, s, l };
}

export function hslToRgb(hsl: Hsl, a = 1): Rgba {
  const c = (1 - Math.abs(2 * hsl.l - 1)) * hsl.s;
  const hp = ((hsl.h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r1, g1, b1] =
    hp < 1
      ? [c, x, 0]
      : hp < 2
        ? [x, c, 0]
        : hp < 3
          ? [0, c, x]
          : hp < 4
            ? [0, x, c]
            : hp < 5
              ? [x, 0, c]
              : [c, 0, x];
  const m = hsl.l - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
    a,
  };
}

/** `#rrggbb`, or `#rrggbbaa` when not fully opaque — `normalizeHexColor`'s form. */
export function rgbaToHex(c: Rgba): string {
  const byte = (v: number) =>
    Math.min(255, Math.max(0, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  const rgb = `#${byte(c.r)}${byte(c.g)}${byte(c.b)}`;
  const alpha = Math.round(Math.min(Math.max(c.a, 0), 1) * 255);
  return alpha >= 255 ? rgb : `${rgb}${alpha.toString(16).padStart(2, "0")}`;
}

/**
 * Where a re-rendered fill lands in dark mode: a narrow band of dark
 * lightnesses, with saturation pulled back. The band is narrow ON PURPOSE — a
 * fill is a background, so what matters is that eight of them stay tellable
 * apart by HUE, not that they span the whole lightness range and half of them
 * glare.
 */
export const DARK_FILL_MIN_L = 0.16;
export const DARK_FILL_MAX_L = 0.34;
export const DARK_FILL_SATURATION = 0.55;
export const DARK_FILL_MAX_S = 0.5;

/**
 * A surface at or above this WCAG luminance is a LIGHT theme, and fills are
 * left exactly as stored.
 */
export const LIGHT_SURFACE_LUMINANCE = 0.5;

/**
 * A fill whose composited luminance is already at or below this is dark
 * enough for a dark theme, and is left alone — so a deliberately dark custom
 * hex renders as the exact color that was typed, and a faint low-opacity wash
 * is not "corrected" into something stronger than it was asked to be.
 */
export const DARK_FILL_KEEP_MAX_LUMINANCE = 0.18;

/**
 * A stored fill (`bg`, or a text `hl`) re-expressed for the surface it will
 * actually be painted on.
 *
 * The problem this solves: a fill is BOARD CONTENT — one value in the CRDT,
 * shared by everyone — while the theme is a per-reader view preference
 * (F-122). The eight presets are pastels chosen against white, so the same
 * stored `#fecaca` that reads as a soft red highlight in light mode is a
 * glaring near-white slab on a `#16181d` canvas. Restyling the presets cannot
 * fix that: whatever is stored, two people are looking at it on two different
 * backgrounds.
 *
 * So the stored value is treated as the fill's IDENTITY ("red"), and each
 * theme renders its own version of it: hue preserved (that is what a reader
 * recognizes), lightness mapped into a dark band, saturation pulled back so a
 * dark fill does not vibrate under light text. Nothing is written back — the
 * doc still holds the color that was picked, and flipping the theme flips the
 * rendering straight back.
 *
 * Pure and surface-driven rather than theme-flag-driven, so callers that
 * already have `--color-surface` (the F-17 readable-ink path) need no new
 * plumbing, and the two can never disagree about which mode is in effect.
 */
export function themedFill(fill: string | undefined, surface: string): string | undefined {
  if (fill === undefined) return undefined;
  const rgba = parseHexColor(fill);
  if (rgba === null || rgba.a === 0) return fill;
  const surfaceRgba = parseHexColor(surface);
  if (surfaceRgba === null) return fill;
  if (relativeLuminance(surfaceRgba) >= LIGHT_SURFACE_LUMINANCE) return fill;
  // Judge the fill as the eye meets it — composited over the surface — so a
  // 20%-opacity pastel, which is already a faint dark tint on a dark board, is
  // not "corrected" into a stronger color than was asked for.
  const seen = compositeOver(rgba, surfaceRgba);
  if (relativeLuminance(seen) <= DARK_FILL_KEEP_MAX_LUMINANCE) return fill;

  const hsl = rgbToHsl(rgba);
  return rgbaToHex(
    hslToRgb(
      {
        h: hsl.h,
        s: Math.min(hsl.s * DARK_FILL_SATURATION, DARK_FILL_MAX_S),
        l: DARK_FILL_MIN_L + (DARK_FILL_MAX_L - DARK_FILL_MIN_L) * hsl.l,
      },
      rgba.a,
    ),
  );
}

/**
 * The color a filled cell's text must take to stay legible, or `undefined`
 * when the theme's own `--color-text` already passes and the CSS default
 * should be left alone.
 *
 * Deliberately conservative — it overrides ONLY when the default fails WCAG AA
 * against the fill as composited over `surface`, so the common cases (an
 * unfilled cell, or a pastel fill in light mode) keep the exact text color
 * every other surface in the app uses, and dark mode on that same pastel flips
 * to dark ink instead of vanishing.
 *
 * The F-42 claim tint / F-70 status wash / F-28 source tint that paint over
 * the fill are ignored here: all three are ≤14% alpha, far too faint to move a
 * pass/fail verdict, and folding them in would make a cell's ink flicker as
 * someone else's cursor moves through it.
 */
export function readableTextColor(
  bg: string | undefined,
  surface: string,
  defaultText: string,
): string | undefined {
  if (bg === undefined) return undefined;
  const fill = parseHexColor(bg);
  // A fully transparent fill shows nothing but the surface the default text
  // was already picked for.
  if (fill === null || fill.a === 0) return undefined;
  const effective = compositeOver(fill, parseHexColor(surface) ?? WHITE);
  const fallback = parseHexColor(defaultText);
  if (fallback !== null && contrastRatio(fallback, effective) >= MIN_TEXT_CONTRAST) {
    return undefined;
  }
  const dark = parseHexColor(INK_DARK);
  const light = parseHexColor(INK_LIGHT);
  if (dark === null || light === null) return undefined; // unreachable: literals
  return contrastRatio(dark, effective) >= contrastRatio(light, effective) ? INK_DARK : INK_LIGHT;
}
